export const UNASSIGNED_RESOURCE_ID = "unassigned";

export const assigneeResourceId = ({ type, id }) => `${type}:${id}`;

export function normalizeActivityAssignees(activity) {
  const source = activity?.assignees || activity?.work_order_assignees || [];
  if (!Array.isArray(source)) return [];

  return source
    .map((assignee) => ({
      type: assignee.type || assignee.assignee_type,
      id: Number(assignee.assignee_id ?? assignee.id),
      assignment_role:
        assignee.assignment_role || assignee.role || "assignee",
      name:
        assignee.name ||
        assignee.assignee_name ||
        [assignee.first_name, assignee.last_name].filter(Boolean).join(" ") ||
        assignee.email ||
        "",
    }))
    .filter(
      (assignee) =>
        ["recipient", "contact"].includes(assignee.type) &&
        Number.isInteger(assignee.id) &&
        assignee.id > 0
    );
}

export function teamResourceIdsForActivity(activity) {
  const assigneeIds = normalizeActivityAssignees(activity)
    .filter(
      (assignee) =>
        assignee.assignment_role !== "owner"
    )
    .map(assigneeResourceId);

  return assigneeIds.length
    ? [...new Set(assigneeIds)]
    : [UNASSIGNED_RESOURCE_ID];
}

export function buildTeamResources(members = []) {
  const resources = new Map();
  const add = (assignee, category = "") => {
    if (assignee.assignment_role === "owner") return;
    const id = assigneeResourceId(assignee);
    if (resources.has(id)) return;
    const label = assignee.name || `${assignee.type === "contact" ? "Contact" : "User"} ${assignee.id}`;
    const subtitle = assignee.type === "contact"
      ? `Contact${category ? ` · ${category.charAt(0).toUpperCase()}${category.slice(1)}` : ""}`
      : "RueBaRue user";
    resources.set(id, {
      id,
      label,
      subtitle,
      filterLabel: `${label} — ${subtitle}`,
    });
  };
  members.filter((member) => !member.deleted && (
    member.assignee_type !== "contact" || ["vendor", "housekeeper", "maintenance", "other", "team"].includes(String(member.category || "").toLowerCase())
  )).forEach((member) => {
    normalizeActivityAssignees({ assignees: [{
      ...member,
      type: member.assignee_type || "recipient",
      assignee_id: member.assignee_id ?? member.recipient_id,
      name: [member.first_name, member.last_name].filter(Boolean).join(" ").trim() || member.name,
    }] }).forEach((assignee) => add(assignee, member.category));
  });
  return [
    { id: UNASSIGNED_RESOURCE_ID, label: "Unassigned", subtitle: "Tasks without an assignee", filterLabel: "Unassigned" },
    ...[...resources.values()].sort((a, b) => a.label.localeCompare(b.label) || a.id.localeCompare(b.id)),
  ];
}

export function groupActivitiesByResources(
  resources,
  activities,
  getResourceIds
) {
  const grouped = {};
  (resources || []).forEach((resource) => {
    grouped[String(resource.id)] = [];
  });

  (activities || []).forEach((activity) => {
    const resourceIds = getResourceIds(activity) || [];
    [...new Set(resourceIds.map(String))].forEach((resourceId) => {
      if (grouped[resourceId]) grouped[resourceId].push(activity);
    });
  });

  return grouped;
}

export function replaceDraggedAssignee(
  assignees,
  sourceResourceId,
  targetResourceId
) {
  const sourceId = String(sourceResourceId || "");
  const target = String(targetResourceId || "");
  const match = target.match(/^(recipient|contact):([1-9]\d*)$/);
  if (target !== UNASSIGNED_RESOURCE_ID && (!match || !Number.isSafeInteger(Number(match[2])))) {
    throw new Error("Invalid task assignee");
  }
  const normalized = normalizeActivityAssignees({ assignees });
  if (normalized.some((assignee) => assignee.assignment_role === "owner" && assigneeResourceId(assignee) === target)) {
    throw new Error("This person is already the task owner. Choose another tasker.");
  }
  const next = normalized.filter(
    (assignee) =>
      !(
        assignee.assignment_role !== "owner" &&
        assigneeResourceId(assignee) === sourceId
      )
  );

  if (
    match &&
    !next.some(
      (assignee) =>
        assignee.assignment_role !== "owner" && assigneeResourceId(assignee) === target
    )
  ) {
    next.push({
      type: match[1],
      id: Number(match[2]),
      assignment_role: "assignee",
      name: "",
    });
  }

  return next.map(({ type, id, assignment_role }) => ({
    type,
    id,
    assignment_role,
  }));
}

export function isActivityOverdue(activity, today) {
  const scheduledDate = String(
    activity?.scheduledDate || activity?.date || ""
  ).slice(0, 10);
  const status = String(activity?.status || "new").toLowerCase();
  return (
    Boolean(scheduledDate) &&
    scheduledDate < today &&
    !["completed", "closed"].includes(status)
  );
}
